import { useEffect, useId, useRef, useState } from "react";
import { cx } from "@/utils/cx";

/**
 * The language control and the currency control, which are the same control.
 *
 * A tertiary button carrying the current choice as a WORD, opening a `.ds-card` menu of the
 * other choices. No flags anywhere: a flag names a country, a language is
 * not a country, and the flag menu is one of the twelve flaws this lane exists to remove
 * (TECH.md flaw 11). The currency control is the same shape with ISO codes.
 *
 * Keyboard: Escape closes and returns focus to the trigger, the arrows move between items, Enter
 * and Space pick, Tab and a click outside close it. `aria-haspopup="menu"` and `aria-expanded`
 * say what it is before it opens.
 */

export interface WordMenuOption {
  value: string;
  label: string;
}

export interface WordMenuProps {
  /** The spoken name of the control, from an i18n key. */
  name: string;
  /** What the trigger reads: the current language in its own words, or "Pay in EUR". */
  triggerLabel: string;
  options: WordMenuOption[];
  value: string;
  onChange: (value: string) => void;
  /** The menu hangs from the right edge of the trigger when the trigger sits at the right. */
  align?: "start" | "end";
  className?: string;
}

export function WordMenu({
  name,
  triggerLabel,
  options,
  value,
  onChange,
  align = "end",
  className,
}: WordMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // The current choice takes focus when the menu opens, so the arrows start where the reader is.
    const index = Math.max(
      0,
      options.findIndex((option) => option.value === value),
    );
    itemRefs.current[index]?.focus();
  }, [open, options, value]);

  const moveFocus = (from: number, delta: number) => {
    const next = (from + delta + options.length) % options.length;
    itemRefs.current[next]?.focus();
  };

  return (
    <div ref={rootRef} className={cx("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        className="ds-btn ds-btn-tertiary ds-pay-hand gap-gk-2"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={name}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
          if (event.key === "Escape" && open) {
            event.preventDefault();
            close(true);
          }
        }}
      >
        {/* The word and nothing else: gate 7.3 and button-tertiary 1.4's last doNot both keep a
            glyph out of a button, and the hairline square already says it is a control. What it
            opens is said by `aria-haspopup` and `aria-expanded`, which is where it belongs. */}
        <span className="whitespace-nowrap">{triggerLabel}</span>
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={name}
          className={cx(
            "ds-card ds-overlay p-gk-1 absolute top-full z-20 mt-gk-1 min-w-gk-30 flex flex-col",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              type="button"
              role="menuitemradio"
              aria-checked={option.value === value}
              className="ds-menu-item"
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  moveFocus(index, 1);
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  moveFocus(index, -1);
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  close(true);
                } else if (event.key === "Tab") {
                  setOpen(false);
                }
              }}
              onClick={() => {
                onChange(option.value);
                close(true);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
